/**
 * "Solo bocca" avatar stream (SPEC_solo_bocca, 2026-10-01).
 *
 * The browser holds the MuseTalk plate (the distinct idle frames, exported by
 * avatar/tools/export_plate_frames.py into /static/avatar_plate/<id>/), so the avatar
 * service only sends which plate frame to show and, while speaking, the JPEG of the
 * mouth zone. `decodeFrame` turns any frame the WS delivers into the bitmap to draw:
 * bare JPEG and v1 exactly as before, v2 = plate frame + crop at (x, y).
 */

import { FRAME_HEADER_BYTES, FRAME_HEADER_V2_BYTES, parseFrameHeader } from './sync';

export interface PlateMeta {
  avatar_id: string;
  frames: number;
  unique: number;
  size: [number, number];
  zona: [number, number, number, number];
  firma: string;
  jpeg_quality: number;
}

/** First text message on /ws/video?mode=bocca (streamout/wsjpeg.py bocca_info). */
export interface BoccaHello {
  mode: string;
  avatar_id?: string;
  frames?: number;
  zona?: number[];
  firma?: string;
}

const PLATE_ROOT = '/static/avatar_plate';
const SAFE_ID = /^[A-Za-z0-9_-]+$/;

/** `?avatar_plate=<id>` from the kiosk URL, or null (absent, empty, or not a plain id). */
export function avatarPlateParam(search: string): string | null {
  const id = new URLSearchParams(search).get('avatar_plate');
  return id && SAFE_ID.test(id) ? id : null;
}

/** Plate cycle index (0..frames-1) → exported frame: the cycle is frames + reversed. */
export function plateFrameIndex(idx: number, frames: number): number {
  if (!(idx >= 0 && idx < frames)) throw new RangeError(`plate idx ${idx} outside 0..${frames - 1}`);
  const half = frames / 2;
  return idx < half ? idx : frames - 1 - idx;
}

export function plateFrameUrl(base: string, i: number): string {
  return `${base}/f${String(i).padStart(3, '0')}.jpg`;
}

/** The service must be serving the very plate we hold (same id, frames, signature). */
export function helloMatchesPlate(hello: BoccaHello, meta: PlateMeta): boolean {
  return (
    hello.mode === 'bocca' &&
    hello.avatar_id === meta.avatar_id &&
    hello.frames === meta.frames &&
    hello.firma === meta.firma
  );
}

export class PlateStore {
  private constructor(
    readonly meta: PlateMeta,
    private readonly frames: ArrayBuffer[],
  ) {}

  /** Fetch plate.json + every frame as JPEG bytes (~32 MB; decoded on present, not here). */
  static async load(avatarId: string): Promise<PlateStore> {
    const base = `${PLATE_ROOT}/${avatarId}`;
    const res = await fetch(`${base}/plate.json`);
    if (!res.ok) throw new Error(`plate.json ${res.status}`);
    const meta = (await res.json()) as PlateMeta;
    const frames = await Promise.all(
      Array.from({ length: meta.unique }, async (_, i) => {
        const r = await fetch(plateFrameUrl(base, i));
        if (!r.ok) throw new Error(`plate frame ${i}: ${r.status}`);
        return r.arrayBuffer();
      }),
    );
    return new PlateStore(meta, frames);
  }

  frameBytes(idx: number): ArrayBuffer {
    return this.frames[plateFrameIndex(idx, this.meta.frames)]!; // plateFrameIndex throws out of range
  }
}

let composeCanvas: OffscreenCanvas | null = null;

/**
 * Bitmap for one WS frame. Bare JPEG / v1: the JPEG as today. v2: the plate frame, plus
 * the mouth crop when the frame carries one. Null if a v2 frame arrives without a plate.
 */
export async function decodeFrame(buf: ArrayBuffer, plate: PlateStore | null): Promise<ImageBitmap | null> {
  const header = parseFrameHeader(new DataView(buf));
  if (!header || header.version < 2) {
    const jpeg = header ? buf.slice(FRAME_HEADER_BYTES) : buf;
    return createImageBitmap(new Blob([jpeg], { type: 'image/jpeg' }));
  }
  if (!plate || header.idx === undefined) return null;
  const base = await createImageBitmap(new Blob([plate.frameBytes(header.idx)], { type: 'image/jpeg' }));
  if (buf.byteLength <= FRAME_HEADER_V2_BYTES) return base; // plate frame as-is
  const crop = await createImageBitmap(new Blob([buf.slice(FRAME_HEADER_V2_BYTES)], { type: 'image/jpeg' }));
  if (!composeCanvas || composeCanvas.width !== base.width || composeCanvas.height !== base.height) {
    composeCanvas = new OffscreenCanvas(base.width, base.height);
  }
  const ctx = composeCanvas.getContext('2d');
  if (!ctx) {
    crop.close();
    return base;
  }
  ctx.drawImage(base, 0, 0);
  ctx.drawImage(crop, header.x ?? 0, header.y ?? 0);
  base.close();
  crop.close();
  return composeCanvas.transferToImageBitmap();
}
